import * as React from 'react';
import _ from 'lodash';
import cx from 'classnames';

import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import Glyph from 'components/Glyph';
import TooltipTrigger from 'components/TooltipTrigger';
import Tooltip from 'components/Tooltip';

import { handleInputChange, handleStripeResponse, setStripeCallStatus } from 'reducers/creditCards';

import {
  injectStripe,
  CardNumberElement,
  CardCVCElement,
  CardExpiryElement,
  PostalCodeElement,
  ReactStripeElements,
} from 'react-stripe-elements';

import './styles/CreditCardInputField.scss';

export type CardInputStatus = {
  cardValid: boolean;
  errorMessage?: string | null;
};

type Props = {
  onCardCreationComplete: (card: any) => void;
  onInputChange: (inputStatus: CardInputStatus) => void;

  // redux
  handleStripeResponse: (sourceId?: string | null, errorMessage?: string | null) => void;
  handleInputChange: (inputStatus: CardInputStatus) => void;
  setStripeCallStatus: (status: string) => void;
  stripeCallStatus?: string;
} & ReactStripeElements.InjectedStripeProps;

type FieldValidity = {
  cardNumber: boolean;
  cardExpiry: boolean;
  cardCvc: boolean;
  postalCode: boolean;
};

type State = {
  focus: boolean;
  fieldValidity: FieldValidity;
};

class CreditCardInputField extends React.Component<Props, State> {
  state: State = {
    focus: false,
    fieldValidity: {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
      postalCode: false,
    },
  };

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.stripeCallStatus === 'start') {
      this.props.setStripeCallStatus('processing');
      this.handleCreateCardSource();
    }
  }

  handleInputChange = (event: ReactStripeElements.ElementChangeResponse) => {
    const { error, complete, elementType } = event;

    const fieldValidity: FieldValidity = { ...this.state.fieldValidity };
    fieldValidity[elementType as keyof FieldValidity] = complete;
    this.setState({ fieldValidity });

    const cardValid = Object.keys(fieldValidity).every(
      field => fieldValidity[field as keyof FieldValidity]
    );

    // TODO: this currently will replace an active error message with null if the user moves onto another field
    // This means that the parent can't correctly show the error message.  But our current
    // cc input doesn't do that either, so I'm moving on to unblock project
    this.props.handleInputChange({ cardValid, errorMessage: _.get(error, 'message') });
  };

  handleCreateCardSource = () => {
    const { stripe } = this.props;

    if (!stripe) {
      console.error('CreditCardInputField tried to use stripe prop but it was null');
      return;
    }

    stripe
      .createSource()
      .then(resp => {
        if (resp.error) {
          this.props.handleStripeResponse(null, resp.error.message);
        } else {
          this.props.handleStripeResponse(resp.source && resp.source.id, null);
        }
      })
      .catch(err => {
        this.props.handleStripeResponse(null, err.message);
      });
  };

  render() {
    // The Stripe elements insert iframes, which we cannot access via class styling (to limit
    // access to the sensitive inputs.  Stripe has built in the ability to style the text
    // within the input by passing in a style prop https://stripe.com/docs/stripe-js/reference#element-options
    const inlineStyle = {
      base: {
        color: '#282c31',
        fontFamily: '"Helvetica Neue", Arial, sans-serif',
        fontSize: '13px',

        '::placeholder': {},
      },
      invalid: {
        color: '#c63442',
      },
    };

    return (
      <div
        className={cx('CreditCardInputField', {
          'CreditCardInputField-focus': this.state.focus,
        })}>
        <div className="CreditCardInputField-inputs">
          <CardNumberElement
            onChange={this.handleInputChange}
            className="CreditCardInputField-input CreditCardInputField-cardNumber"
            style={inlineStyle}
            onFocus={() => this.setState({ focus: true })}
            onBlur={() => this.setState({ focus: false })}
          />
          <CardExpiryElement
            onChange={this.handleInputChange}
            className="CreditCardInputField-input CreditCardInputField-expiry"
            style={inlineStyle}
            onFocus={() => this.setState({ focus: true })}
            onBlur={() => this.setState({ focus: false })}
          />
          <CardCVCElement
            onChange={this.handleInputChange}
            className="CreditCardInputField-input CreditCardInputField-verificationCode"
            style={inlineStyle}
            onFocus={() => this.setState({ focus: true })}
            onBlur={() => this.setState({ focus: false })}
          />
          <PostalCodeElement
            placeholder="ZIP"
            onChange={this.handleInputChange}
            className="CreditCardInputField-input CreditCardInputField-zip"
            style={inlineStyle}
            onFocus={() => this.setState({ focus: true })}
            onBlur={() => this.setState({ focus: false })}
          />
        </div>
        <div className="CreditCardInputField-inputs">
          <TooltipTrigger
            placement="top"
            tooltip={
              <Tooltip>All data is encrypted and protected with bank-level security</Tooltip>
            }>
            <Glyph name="lock" className="CreditCardInputField-icon" size="small" color="koala" />
          </TooltipTrigger>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state: any) {
  return {
    stripeCallStatus: state.creditCards.stripeCallStatus,
  };
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
  return bindActionCreators(
    {
      handleInputChange,
      handleStripeResponse,
      setStripeCallStatus,
    },
    dispatch
  );
}

export { CreditCardInputField };
export default injectStripe(
  connect(
    mapStateToProps,
    mapDispatchToProps
    // @ts-ignore
  )(CreditCardInputField)
);
